import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import {
  Star, Send, Loader2, MapPin, User, MessageSquare,
  Lightbulb, Quote, ChevronLeft, ChevronRight, ArrowLeft, Trash2,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const StarRating = ({ rating, onRate, interactive = false, size = 'w-5 h-5' }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type={interactive ? 'button' : undefined}
        onClick={interactive ? () => onRate(star) : undefined}
        className={interactive ? 'cursor-pointer transition-transform hover:scale-110' : 'cursor-default'}
        data-testid={interactive ? `star-${star}` : undefined}
      >
        <Star
          className={`${size} ${star <= rating ? 'fill-amber-400 text-amber-400' : 'fill-none text-gray-300'}`}
        />
      </button>
    ))}
  </div>
);

const ReviewCard = ({ review, index, isAdmin, onDelete }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.05 }}
    className="bg-white/95 backdrop-blur-xl border border-white/30 rounded-2xl p-6 hover:shadow-lg transition-shadow overflow-hidden relative group"
    data-testid={`review-card-${review.id}`}
  >
    {isAdmin && (
      <button
        onClick={() => onDelete(review)}
        className="absolute top-3 right-3 p-1.5 rounded-lg bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-600 transition-all"
        data-testid={`delete-review-${review.id}`}
        title="Delete review"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    )}
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
          {review.name?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-ocean text-sm truncate">{review.name}</p>
          {review.location && (
            <p className="text-xs text-stone-500 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {review.location}
            </p>
          )}
        </div>
      </div>
      <StarRating rating={review.rating} size="w-4 h-4" />
    </div>
    <p className="text-charcoal dark:text-gray-300 text-sm leading-relaxed mb-2">{review.comment}</p>
    {review.suggestion && (
      <div className="mt-3 pt-3 border-t border-stone-100">
        <p className="text-xs text-teal-700 font-medium flex items-center gap-1 mb-1">
          <Lightbulb className="w-3 h-3" /> Suggestion
        </p>
        <p className="text-stone-500 text-xs leading-relaxed">{review.suggestion}</p>
      </div>
    )}
    <p className="text-xs text-stone-400 mt-3">{new Date(review.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
  </motion.div>
);

const WebsiteReviewsPage = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, pages: 1, avg_rating: 0, total_reviews: 0 });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [suggestion, setSuggestion] = useState('');

  useEffect(() => {
    if (isAuthenticated && user) {
      const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
      if (fullName) setName(fullName);
    }
  }, [isAuthenticated, user]);

  const fetchReviews = useCallback(async (p = 1) => {
    try {
      const res = await api.get(`/website-reviews?page=${p}&limit=12`);
      if (res.data.success) {
        setReviews(res.data.reviews);
        setMeta({
          total: res.data.total,
          pages: res.data.pages,
          avg_rating: res.data.avg_rating,
          total_reviews: res.data.total_reviews,
        });
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchReviews(page);
  }, [page, fetchReviews]);

  const handleDeleteReview = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/website-reviews/${deleteTarget.id}`);
      setReviews(reviews.filter((r) => r.id !== deleteTarget.id));
      toast.success('Review deleted');
    } catch {
      toast.error('Failed to delete review');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Please enter your name'); return; }
    if (rating === 0) { toast.error('Please select a star rating'); return; }
    if (!comment.trim()) { toast.error('Please write a review comment'); return; }

    setSubmitting(true);
    try {
      await api.post('/website-reviews', {
        name: name.trim(),
        location: location.trim(),
        rating,
        comment: comment.trim(),
        suggestion: suggestion.trim(),
        user_id: isAuthenticated ? user?.id : null,
      });
      toast.success('Thank you for your review!');
      setRating(0);
      setComment('');
      setSuggestion('');
      if (!isAuthenticated) { setName(''); setLocation(''); }
      setPage(1);
      fetchReviews(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="website-reviews-page">
      {/* Premium Background — matching Contact page */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70" />
        <div className="absolute top-32 right-20 w-96 h-96 bg-gold/15 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-40 left-10 w-80 h-80 bg-sky/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}} />
      </div>

      <Header />

      <main className="pt-24 pb-16 px-4 sm:px-6 md:px-12 lg:px-24 relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Back to Dashboard — top (logged in only) */}
          {isAuthenticated && (
            <div className="mb-6">
              <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white text-sm flex items-center gap-1 transition-colors" data-testid="back-to-dashboard-top">
                <ArrowLeft className="w-4 h-4" /> Back to Dashboard
              </button>
            </div>
          )}

          {/* Hero */}
          <div className="text-center mb-10">
            <div className="w-14 h-14 rounded-2xl bg-gold/20 flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Quote className="w-7 h-7 text-gold" />
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl text-white drop-shadow-lg mb-2" data-testid="reviews-page-title">
              What Our Users Say
            </h1>
            <p className="text-white/70 max-w-lg mx-auto">
              Share your experience with Triple A Travel Solutions and help others plan their perfect trip.
            </p>
            {meta.total_reviews > 0 && (
              <div className="flex items-center justify-center gap-3 mt-4">
                <StarRating rating={Math.round(meta.avg_rating)} size="w-5 h-5" />
                <span className="text-lg font-bold text-white">{meta.avg_rating}</span>
                <span className="text-white/40">|</span>
                <span className="text-sm text-white/60">{meta.total_reviews} review{meta.total_reviews !== 1 ? 's' : ''}</span>
              </div>
            )}
          </div>

          {/* Review Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/95 backdrop-blur-xl border border-white/30 rounded-2xl p-6 sm:p-8 mb-10 shadow-xl"
            data-testid="review-form"
          >
            <h2 className="font-serif text-xl text-ocean mb-5 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-teal-600" />
              Write a Review
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-stone-700 text-sm flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-stone-400" /> Your Name *
                  </Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Doe"
                    className="h-11 border-stone-200 focus:border-teal-500 focus:ring-teal-500/20"
                    data-testid="review-name-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-stone-700 text-sm flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-stone-400" /> Location <span className="text-stone-400 font-normal">(optional)</span>
                  </Label>
                  <Input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g., Sydney, Australia"
                    className="h-11 border-stone-200 focus:border-teal-500 focus:ring-teal-500/20"
                    data-testid="review-location-input"
                  />
                </div>
              </div>

              {/* Star Rating */}
              <div className="space-y-1.5">
                <Label className="text-stone-700 text-sm">Rating *</Label>
                <div className="flex items-center gap-2">
                  <StarRating rating={rating} onRate={setRating} interactive size="w-8 h-8" />
                  {rating > 0 && (
                    <span className="text-sm text-stone-500 ml-2">
                      {['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'][rating]}
                    </span>
                  )}
                </div>
              </div>

              {/* Review Comment */}
              <div className="space-y-1.5">
                <Label className="text-stone-700 text-sm flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5 text-stone-400" /> Your Review *
                </Label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Tell us about your experience using Triple A Travel Solutions..."
                  className="w-full h-28 p-3 rounded-lg border border-stone-200 text-stone-800 text-sm placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 resize-none"
                  data-testid="review-comment-input"
                />
              </div>

              {/* Suggestion */}
              <div className="space-y-1.5">
                <Label className="text-stone-700 text-sm flex items-center gap-1.5">
                  <Lightbulb className="w-3.5 h-3.5 text-amber-500" /> Any suggestion for improvement? <span className="text-stone-400 font-normal">(optional)</span>
                </Label>
                <textarea
                  value={suggestion}
                  onChange={(e) => setSuggestion(e.target.value)}
                  placeholder="Share ideas on how we can make your travel planning experience even better..."
                  className="w-full h-20 p-3 rounded-lg border border-stone-200 text-stone-800 text-sm placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 resize-none"
                  data-testid="review-suggestion-input"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="bg-gradient-to-r from-teal-600 to-teal-700 text-white hover:from-teal-700 hover:to-teal-800 rounded-full px-8 h-11 font-semibold shadow-md"
                data-testid="submit-review-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Submit Review
              </Button>
            </form>
          </motion.div>

          {/* Reviews List */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-16">
              <Star className="w-12 h-12 text-stone-200 mx-auto mb-3" />
              <p className="text-stone-500">No reviews yet. Be the first to share your experience!</p>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {reviews.map((review, i) => (
                  <ReviewCard key={review.id} review={review} index={i} isAdmin={isAdmin} onDelete={setDeleteTarget} />
                ))}
              </div>

              {meta.pages > 1 && (
                <div className="flex items-center justify-center gap-3 mt-8">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="rounded-full h-9 w-9 p-0"
                    data-testid="reviews-prev-page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-stone-500">
                    Page {page} of {meta.pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(meta.pages, p + 1))}
                    disabled={page >= meta.pages}
                    className="rounded-full h-9 w-9 p-0"
                    data-testid="reviews-next-page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Back to Dashboard — bottom (logged in only) */}
          {isAuthenticated && (
            <div className="mt-10 text-center">
              <Button onClick={() => navigate('/dashboard')} variant="outline"
                className="text-white border-white/30 hover:bg-white/10 rounded-full px-6"
                data-testid="back-to-dashboard-bottom">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Review?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the review by <strong>{deleteTarget?.name}</strong>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-review">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteReview}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
              data-testid="confirm-delete-review"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WebsiteReviewsPage;
